import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { LayoutService } from '@/layout/service/layout.service';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { PessoaFotoDialogComponent } from '@/shared/components/pessoa/pessoa-foto-dialog/pessoa-foto-dialog';
import { PaginatorModule } from 'primeng/paginator';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { ConfirmationService } from 'primeng/api';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { UsuarioDetalhe } from '../dialogs/usuario-detalhe/usuario-detalhe';
import { AtribuirRoles } from '../permissoes/atribuir-roles/atribuir-roles';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { DialogModule } from 'primeng/dialog';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface OpcaoOrdenacao {
    label: string;
    field: 'nmSolicitante' | 'ufSolicitante';
    direction: 'asc' | 'desc';
}

@Component({
    selector: 'app-usuarios-lista',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        ButtonModule,
        TableModule,
        TagModule,
        TooltipModule,
        InputTextModule,
        ConfirmDialogModule,
        SelectModule,
        ToggleSwitchModule,
        PessoaAvatarComponent,
        PessoaFotoDialogComponent,
        PaginatorModule,
        TableViewSwitcherComponent,
        SelectEstado,
        CardContainer,
        CardContent,
        UsuarioDetalhe,
        AtribuirRoles,
        CanDirective,
        DialogModule,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [ConfirmationService],
    templateUrl: './usuarios-lista.html',
    styleUrl: './usuarios-lista.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Lista de usuários do portal (filtro/ordenação client-side), com novo/editar e modal de permissões. */
export class UsuariosLista implements OnInit {
    private readonly usuarioService = inject(UsuarioService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly layoutService = inject(LayoutService);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly permissionService = inject(PermissionService);

    protected readonly _usuarios = signal<UsuarioResumoResponse[]>([]);
    protected readonly carregando = signal(false);

    /** Filtros aplicados (após "Filtrar"). */
    protected readonly filtroNome = signal('');
    protected readonly filtroStatus = signal<string | null>(null);
    /** Inputs pendentes (aplicados no botão "Filtrar"). */
    protected readonly filtroNomeInput = signal('');
    protected readonly filtroStatusInput = signal<string | null>(null);

    /** Filtro por UF (client-side por `ufSolicitante`) — pendente (bound) + aplicado no "Filtrar". */
    protected readonly ufUsuario = signal('');
    protected readonly ufFiltro = signal('');
    protected readonly filtrarPorUf = signal(false);
    protected readonly ufFiltroAplicado = signal('');
    protected readonly filtrarPorUfAplicado = signal(false);
    protected readonly isUsuarioBR = computed(() => this.ufUsuario() === 'BR');

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    /** Ordenação — pendente (bound) + aplicada no "Filtrar". */
    protected readonly sortOpcoes: OpcaoOrdenacao[] = [
        { label: 'Nome (A→Z)', field: 'nmSolicitante', direction: 'asc' },
        { label: 'Nome (Z→A)', field: 'nmSolicitante', direction: 'desc' },
        { label: 'UF (A→Z)', field: 'ufSolicitante', direction: 'asc' },
        { label: 'UF (Z→A)', field: 'ufSolicitante', direction: 'desc' }
    ];
    protected readonly ordenacao = signal<OpcaoOrdenacao>(this.sortOpcoes[0]);
    protected readonly ordenacaoAplicada = signal<OpcaoOrdenacao>(this.sortOpcoes[0]);

    protected readonly viewMode = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly cardPage = signal(0);
    protected readonly cardPageSize = signal(15);

    protected readonly usuariosPaginados = computed(() => {
        const inicio = this.cardPage() * this.cardPageSize();
        return this.usuariosFiltrados().slice(inicio, inicio + this.cardPageSize());
    });

    protected readonly opcoesStatus = [
        { label: 'Ativo', value: 'S' },
        { label: 'Inativo', value: 'N' }
    ];

    protected readonly totalAtivos = computed(() => this._usuarios().filter((u) => u.inAtivo === 'S').length);
    protected readonly totalInativos = computed(() => this._usuarios().filter((u) => u.inAtivo === 'N').length);

    protected readonly usuariosFiltrados = computed(() => {
        const termo = this.filtroNome().toLowerCase().trim();
        const status = this.filtroStatus();
        const filtrarUf = this.isUsuarioBR() && this.filtrarPorUfAplicado() && !!this.ufFiltroAplicado();
        const uf = this.ufFiltroAplicado().toUpperCase();
        const lista = this._usuarios().filter((u) => {
            const matchTermo = !termo || u.nmSolicitante?.toLowerCase().includes(termo) || u.ufSolicitante?.toLowerCase().includes(termo) || u.userId?.toLowerCase().includes(termo);
            const matchStatus = !status || u.inAtivo === status;
            const matchUf = !filtrarUf || (u.ufSolicitante ?? '').toUpperCase() === uf;
            return matchTermo && matchStatus && matchUf;
        });
        const { field, direction } = this.ordenacaoAplicada();
        const dir = direction === 'asc' ? 1 : -1;
        return [...lista].sort((a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'pt-BR', { sensitivity: 'base' }) * dir);
    });

    protected modalDetalhe = signal(false);
    protected modalPermissoes = signal(false);
    protected usuarioSelecionado = signal<UsuarioResumoResponse | null>(null);
    protected userIdPermissoes = signal<string | null>(null);
    protected usuarioPermissoes = signal<UsuarioResumoResponse | null>(null);
    protected readonly rolePermAdmin = PERMISSOES.configuracoes.usuarios.permissoes.administrar;
    protected readonly roleUsuariosCriar = PERMISSOES.configuracoes.usuarios.criar;
    protected readonly roleUsuariosEditar = PERMISSOES.configuracoes.usuarios.editar;
    protected readonly roleUsuariosAlterarStatus = PERMISSOES.configuracoes.usuarios.alterarStatus;

    /** Foto em exibição no diálogo de ampliação; `null` mantém o diálogo fechado. */
    protected readonly fotoModalUrl = signal<string | null>(null);
    protected readonly nomeFotoAmpliada = signal('');

    /** Amplia a foto que o avatar emitiu, anunciando de quem ela é. */
    protected ampliarFoto(url: string, usuario: UsuarioResumoResponse): void {
        this.nomeFotoAmpliada.set(usuario.nmSolicitante || '');
        this.fotoModalUrl.set(url);
    }

    ngOnInit(): void {
        // Regional: a UF do operador já é conhecida sem ida ao servidor — o filtro nasce nela,
        // travado, e o perfil não a muda.
        const ufOperador = this.ufOperador();
        if (ufOperador) {
            this.ufUsuario.set(ufOperador);
            this.ufFiltro.set(ufOperador);
            this.filtrarPorUf.set(true);
        }

        this.carregarUsuarios();
        this.perfilService
            .buscarPerfil()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const perfil = Array.isArray(res) ? res[0] : res;
                    const uf = ufOperador || (perfil?.uf ?? '').trim().toUpperCase();
                    this.ufUsuario.set(uf);
                    this.ufFiltro.set(uf);
                },
                error: () => this.ufUsuario.set(ufOperador)
            });
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected aplicarFiltros(): void {
        this.filtroNome.set(this.filtroNomeInput());
        this.filtroStatus.set(this.filtroStatusInput());
        this.filtrarPorUfAplicado.set(this.filtrarPorUf());
        this.ufFiltroAplicado.set(this.ufFiltro());
        this.ordenacaoAplicada.set(this.ordenacao());
        this.cardPage.set(0);
        this.carregarUsuarios();
    }

    protected limparFiltros(): void {
        this.filtroNomeInput.set('');
        this.filtroNome.set('');
        this.filtroStatusInput.set(null);
        this.filtroStatus.set(null);
        // Limpar não destrava a UF do regional: ela volta para a dele, não para "todas".
        const ufOperador = this.ufOperador();
        this.filtrarPorUf.set(!!ufOperador);
        this.filtrarPorUfAplicado.set(false);
        this.ufFiltro.set(ufOperador || this.ufUsuario());
        this.ufFiltroAplicado.set(ufOperador || this.ufUsuario());
        this.ordenacao.set(this.sortOpcoes[0]);
        this.ordenacaoAplicada.set(this.sortOpcoes[0]);
        this.cardPage.set(0);
        this.carregarUsuarios();
    }

    protected onEstadoChange(uf: string): void {
        this.ufFiltro.set((uf ?? '').toUpperCase());
    }

    protected onFiltrarPorUfChange(valor: boolean): void {
        this.filtrarPorUf.set(valor);
    }

    private carregarUsuarios(): void {
        this.carregando.set(true);
        this.usuarioService
            .listar({ size: 200 })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (page) => {
                    this._usuarios.set(page.content);
                    this.carregando.set(false);
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar usuários' });
                    this.carregando.set(false);
                }
            });
    }

    protected novoUsuario(): void {
        this.usuarioSelecionado.set(null);
        this.modalDetalhe.set(true);
    }

    protected editarUsuario(usuario: UsuarioResumoResponse): void {
        this.usuarioSelecionado.set(usuario);
        this.modalDetalhe.set(true);
    }

    protected gerenciarPermissoes(usuario: UsuarioResumoResponse): void {
        this.userIdPermissoes.set(usuario.userId);
        this.usuarioPermissoes.set(usuario);
        this.modalPermissoes.set(true);
    }

    protected confirmarToggleStatus(usuario: UsuarioResumoResponse): void {
        const acao = usuario.inAtivo === 'S' ? 'inativar' : 'ativar';
        this.confirmationService.confirm({
            message: `Deseja ${acao} o usuário "${usuario.nmSolicitante || usuario.userId}"?`,
            header: 'Confirmação',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => this.toggleStatus(usuario)
        });
    }

    private toggleStatus(usuario: UsuarioResumoResponse): void {
        this.usuarioService
            .ativarInativar(usuario.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (atualizado) => {
                    const label = atualizado.inAtivo === 'S' ? 'ativado' : 'inativado';
                    this.notificacoes.notificar({ severity: 'success', summary: `Usuário ${label} com sucesso.` });
                    this.carregarUsuarios();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao alterar status do usuário' })
            });
    }

    protected onUsuarioSalvo(): void {
        this.modalDetalhe.set(false);
        this.carregarUsuarios();
    }

    protected statusSeverity(inAtivo: string): 'success' | 'danger' {
        return inAtivo === 'S' ? 'success' : 'danger';
    }

    protected onCardPageChange(event: { first?: number; rows?: number }): void {
        const rows = event.rows ?? this.cardPageSize();
        this.cardPageSize.set(rows);
        this.cardPage.set(Math.floor((event.first ?? 0) / rows));
    }

    protected iniciais(usuario: UsuarioResumoResponse): string {
        const nome = usuario.nmSolicitante || usuario.userId || '?';
        return nome
            .split(' ')
            .slice(0, 2)
            .map((p) => p[0])
            .join('')
            .toUpperCase();
    }
}
